// Async turn-based multiplayer client module.
//
// Manages the lifecycle of an async game from the client side:
// - Capturing emulator state on turn end
// - Uploading state to the server
// - Downloading and restoring state on turn start
// - Auto-detecting turn end via lives monitoring (when livesSpec available)
// - Showing the friend picker for initial challenge

import { escapeHtml } from '../shared/escape-html.js';
import { fetchSystemDefinitions } from './achievements/definitions.js';
import { captureEmulatorState } from './modules/save-manager.js';
import { isConnectionCancellation } from './network.js';

export class AsyncGameManager {
  constructor(app) {
    this.app = app;
    this.activeGame = null;
    this._livesSpec = null;
    this._lastLives = null;
    this._turnStartFrame = 0;
    this._turnStartTime = 0;
    this._endingTurn = false;
    this._pendingTurnState = null;
    this._pendingChallengeState = null;
    this._preparingChallenge = false;
  }

  get isActive() { return !!this.activeGame; }

  /**
   * Deterministic WebSocket room code for the live baton-pass. Both
   * participants of an async game converge on the same code so the
   * opponent can opt to spectate without any handshake.
   */
  static roomCodeFor(gameId) { return 'async:' + gameId; }

  startTurn(game) {
    this.activeGame = game;
    this._turnStartFrame = this.app.soloFrame || 0;
    this._turnStartTime = Date.now();
    this._lastLives = null;
    this._endingTurn = false;
    this._skipPostEndTurnExit = false;
    if (this._postEndTurnExitTimer) {
      clearTimeout(this._postEndTurnExitTimer);
      this._postEndTurnExitTimer = null;
    }
    if (this._liveBatonFallbackTimer) {
      clearTimeout(this._liveBatonFallbackTimer);
      this._liveBatonFallbackTimer = null;
    }
    this._showEndTurnButton();
    this._loadLivesSpec();
    this._ensureLiveRoom();
  }

  clear() {
    this.activeGame = null;
    this._livesSpec = null;
    this._lastLives = null;
    this._endingTurn = false;
    this._pendingTurnState = null;
    this._pendingChallengeState = null;
    this._preparingChallenge = false;
    this._hideEndTurnButton();
  }

  /**
   * Create or rejoin the WebSocket room for this game so the opponent can
   * watch live. The room is private (server-side participant auth) and
   * lazy in cost — SoloStreamHost defers WebRTC encoding until a spectator
   * actually joins. Safe to call when already connected (no-op).
   */
  async _ensureLiveRoom() {
    if (!this.activeGame?.id) return;
    const launchOwner = this.app._gameLaunchIntentGeneration;
    const ownsLaunch = () => launchOwner === undefined
      || this.app._isGameLaunchIntentCurrent?.(launchOwner) !== false;
    const code = AsyncGameManager.roomCodeFor(this.activeGame.id);
    const net = this.app.net;
    if (!net) return;
    if (net.connected && net.roomCode === code) return;
    try {
      const resp = await fetch('/api/rooms', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code,
          system: this.activeGame.system_id,
          romTitle: this.activeGame.rom_name || null,
          saveCoreName: this.app.emu?.coreName || undefined,
        }),
      });
      if (!ownsLaunch()) return;
      if (!resp.ok) {
        // 409 room_exists means we already own this room from a prior
        // turn — that's fine, just reconnect to it.
        const body = await resp.json().catch(() => ({}));
        if (!ownsLaunch()) return;
        if (resp.status !== 409 || body.error !== 'room_exists') {
          console.warn('[async-game] live room create failed:', body.error || resp.status);
          return;
        }
      }
      // Publish before connect because assigned can arrive while it awaits.
      this.app._roomJoinLaunchOwner = launchOwner ?? null;
      await net.connect(code);
      if (!ownsLaunch()) {
        if (this.app.net === net
            && net.roomCode === code
            && this.app._roomJoinLaunchOwner === (launchOwner ?? null)) {
          net.disconnect(4000);
          this.app._roomJoinLaunchOwner = null;
        }
        return;
      }
      this.app._soloRoomCode = code;
      this.app._startSoloStream?.();
    } catch (err) {
      if (!ownsLaunch() || isConnectionCancellation(err)) return;
      console.warn('[async-game] live room setup failed:', err.message);
    }
  }

  /** Tear down the live room (on natural turn end / exit to menu). */
  teardownLiveRoom() {
    const net = this.app.net;
    if (!net?.connected) return;
    const code = AsyncGameManager.roomCodeFor(this.activeGame?.id || '');
    if (net.roomCode !== code && !net.roomCode?.startsWith('async:')) return;
    this.app._stopSoloStream?.();
    try { net.disconnect(4000); } catch {}
    if (this.app._soloRoomCode === net.roomCode) this.app._soloRoomCode = null;
  }

  _pauseGame() {
    const app = this.app;
    app.running = false;
    if (app.animFrameId) {
      cancelAnimationFrame(app.animFrameId);
      app.animFrameId = null;
    }
    if (app.emu?.selfDriving && typeof app.emu.module?.pauseMainLoop === 'function') {
      app.emu.module.pauseMainLoop();
      this._wasmPaused = true;
    }
  }

  /**
   * Tap START on the core so the GAME pauses itself behind our modal.
   *
   * These frames go through the same observe → step → soloFrame++ discipline as
   * the solo loop, because the replay recorder's contract is that every frame
   * the core steps is a frame the log records. A frame stepped off the books
   * desyncs the server's re-execution from that point to the end of the run —
   * and since this frame carries a START press, what desyncs is the game's
   * PAUSE parity: the verifier replays a run that is paused where the player
   * was playing, so it never verifies and every clip rendered from it is a
   * frozen PAUSE screen.
   *
   * Self-driving libretro cores keep the bare injection: their stepFrame() is a
   * no-op (the core runs its own rAF loop, so nothing is stepped off the books)
   * and soloFrame isn't their frame counter — they record via the ring instead.
   */
  _injectInGamePause() {
    const app = this.app;
    const emu = app.emu;
    if (!emu?.loaded) return;
    const startBit = app.system?.buttons?.START;
    if (!startBit) return;
    const jsStepped = !emu.selfDriving;
    if (jsStepped && (typeof app._gameplayHeld === 'function' ? app._gameplayHeld() : app._stateCapturePending)) return;
    for (const mask of [startBit, 0]) {
      if (jsStepped && app.replayRecorder?.isRecording) {
        if (app.replayRecorder.observe(app.soloFrame, mask, 0) === false) {
          app._replayRecordingUnavailable?.('replay_capacity_exceeded');
        }
      }
      emu.setButtons(0, mask);
      emu.stepFrame();
      if (jsStepped) app.soloFrame++;
    }
  }

  _resumeGame() {
    const app = this.app;
    if (this._wasmPaused && typeof app.emu?.module?.resumeMainLoop === 'function') {
      app.emu.module.resumeMainLoop();
      this._wasmPaused = false;
    } else if (app.state === 'solo' && app.emu?.loaded && !app.running) {
      app.running = true;
      app.lastFrameTime = performance.now();
      app._soloLoop(performance.now());
    }
  }

  /**
   * Capture the handoff state while a self-driving core's main loop can still
   * service its async pump, then stop the runtime only after valid bytes exist.
   * The synthetic START press keeps the uploaded state paused in-game; if the
   * capture fails, a second press restores gameplay before returning.
   */
  async _captureBeforePause({ isLaunchCurrent = null } = {}) {
    this._injectInGamePause();
    let state = null;
    try {
      state = await captureEmulatorState(this.app);
    } catch (err) {
      console.warn('[async-game] state capture failed:', err);
    }
    if (isLaunchCurrent && !isLaunchCurrent()) return null;
    if (!state) {
      this._injectInGamePause();
      return null;
    }
    this._pauseGame();
    return state;
  }

  _showEndTurnButton() {
    document.getElementById('btn-end-turn')?.classList.remove('hidden');
  }

  _hideEndTurnButton() {
    document.getElementById('btn-end-turn')?.classList.add('hidden');
  }

  async _loadLivesSpec() {
    if (!this.activeGame) return;
    try {
      const { system_id, rom_hash } = this.activeGame;
      // Merge-aware (gb≡gbc): a GBC game plays under gb but its definitions are
      // in gbc.json, so go through fetchSystemDefinitions which concatenates both.
      const data = await fetchSystemDefinitions(system_id);
      const game = data.games?.find(g =>
        g.roms?.some(r => r.hash === rom_hash)
      );
      if (game?.livesSpec) {
        this._livesSpec = game.livesSpec;
      }
    } catch {}
  }

  tick(frame) {
    if (!this.isActive || !this._livesSpec || this._endingTurn) return;
    if (frame % 4 !== 0) return;

    const core = this.app.emu;
    if (!core?.readRAM) return;

    try {
      const { addr, size, zeroValue } = this._livesSpec;
      const lives = this._readValue(core, addr, size);
      if (lives === null) return;

      if (this._lastLives === null) {
        this._lastLives = lives;
        return;
      }

      if (lives === (zeroValue ?? 0) && this._lastLives !== (zeroValue ?? 0)) {
        this._lastLives = lives;
        this._autoEndTurn();
      } else {
        this._lastLives = lives;
      }
    } catch {}
  }

  _readValue(core, addr, size) {
    const sizeMap = { u8: 1, u16_le: 2, u16_be: 2 };
    const len = sizeMap[size] || 1;
    const bytes = core.readRAM(addr, len);
    if (!bytes || !bytes.length) return null;

    if (size === 'u16_le') return bytes[0] | (bytes[1] << 8);
    if (size === 'u16_be') return (bytes[0] << 8) | bytes[1];
    return bytes[0];
  }

  async _autoEndTurn() {
    if (this._endingTurn) return;
    if (this.app.system?.id === 'n64') {
      this.app._flash('Async challenges aren’t available for N64 yet');
      return;
    }
    this._endingTurn = true;
    const state = await this._captureBeforePause();
    if (!state) {
      this.app._flash('Could not capture game state');
      this._endingTurn = false;
      return;
    }
    this._pendingTurnState = state;
    this.app._flash('You lost a life, ending turn...', 3000);
    setTimeout(() => {
      if (!this._endingTurn) return;
      this.endTurn('death');
    }, 2000);
  }

  async endTurn(reason = 'manual') {
    this._endingTurn = true;
    this._skipPostEndTurnExit = false;
    if (this._postEndTurnExitTimer) {
      clearTimeout(this._postEndTurnExitTimer);
      this._postEndTurnExitTimer = null;
    }
    if (this._liveBatonFallbackTimer) {
      clearTimeout(this._liveBatonFallbackTimer);
      this._liveBatonFallbackTimer = null;
    }

    const game = this.activeGame;
    const core = this.app.emu;
    if (!core?.loaded) {
      this.app._flash('No game running');
      this._endingTurn = false;
      return;
    }

    // N64 serializes only via the in-loop pump (saveStateN64), but async-game
    // pauses the main loop before capturing, so the pump can never fire.
    // Refuse rather than hang or submit an empty turn. See docs/n64-save-handoff.md.
    if (this.app.system?.id === 'n64') {
      this.app._flash('Async challenges aren’t available for N64 yet');
      this._endingTurn = false;
      return;
    }

    const state = this._pendingTurnState || await this._captureBeforePause();
    this._pendingTurnState = null;
    if (!state) {
      this.app._flash('Could not capture game state');
      this._endingTurn = false;
      return;
    }

    const stateBytes = this._serializeState(state);
    const frameCount = (this.app.soloFrame || this.app._libretroSoloFrame || 0) - this._turnStartFrame;
    const durationSeconds = Math.round((Date.now() - this._turnStartTime) / 1000);

    try {
      const params = new URLSearchParams({
        end_reason: reason,
        frame_count: String(frameCount),
        duration_seconds: String(durationSeconds),
        core_name: this.app.emu?.coreName || '',
      });

      const url = game.id
        ? `/api/async-games/${game.id}/end-turn?${params}`
        : `/api/async-games?${params}&to_username=${encodeURIComponent(game.to_username)}&system_id=${encodeURIComponent(game.system_id)}&rom_hash=${encodeURIComponent(game.rom_hash)}&rom_name=${encodeURIComponent(game.rom_name || '')}`;

      const resp = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/octet-stream' },
        body: stateBytes,
      });

      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        this.app._flash(body.error || 'Failed to send turn');
        this._resumeGame();
        this._endingTurn = false;
        return;
      }

      const result = await resp.json();
      this.app._flash('Turn sent! Your opponent has been notified.', 4000);
      if (result.live_baton) {
        this._skipPostEndTurnExit = true;
        if (game.id && typeof this.app._batonBecomeSpectator === 'function') {
          this._liveBatonFallbackTimer = setTimeout(() => {
            this._liveBatonFallbackTimer = null;
            if (!this._skipPostEndTurnExit || this.app.state !== 'solo') return;
            this.app._batonBecomeSpectator({
              type: 'baton_pass',
              role: 'become_spectator',
              game_id: game.id,
              turn_number: result.turn_number,
              room_code: AsyncGameManager.roomCodeFor(game.id),
            });
          }, 500);
        }
      }
      this.clear();

      // Brief grace window: if the opponent was already spectating live,
      // the server's `baton_pass` arrives within ms and the handler over
      // in main.js sets `_skipPostEndTurnExit` so this user stays in the
      // (now-spectator) view instead of being yanked to the menu.
      this._postEndTurnExitTimer = setTimeout(() => {
        this._postEndTurnExitTimer = null;
        if (this._skipPostEndTurnExit) {
          this._skipPostEndTurnExit = false;
          return;
        }
        this.app._exitToMenu();
      }, 2500);

      return result;
    } catch (err) {
      console.error('[async-game] end-turn failed:', err);
      this.app._flash('Network error. Try again');
      this._resumeGame();
      this._endingTurn = false;
    }
  }

  async loadAndResume(gameId, { isLaunchCurrent = null } = {}) {
    const ownsLaunch = () => !isLaunchCurrent || isLaunchCurrent();
    // A failed or superseded request must never leave an older mailbox payload
    // available for the next caller to consume.
    this._pendingResume = null;
    if (!ownsLaunch()) return false;
    try {
      const detailResp = await fetch(`/api/async-games/${gameId}`, { credentials: 'include' });
      if (!ownsLaunch()) return false;
      if (!detailResp.ok) {
        this.app._flash('Could not load game');
        return false;
      }
      const game = await detailResp.json();
      if (!ownsLaunch()) return false;

      if (!game.is_my_turn) {
        this.app._flash("It's not your turn yet");
        return false;
      }

      if (game.status === 'awaiting_p2') {
        const acceptResp = await fetch(`/api/async-games/${gameId}/accept`, {
          method: 'POST',
          credentials: 'include',
        });
        if (!ownsLaunch()) return false;
        if (!acceptResp.ok) {
          this.app._flash('Could not accept challenge');
          return false;
        }
      }

      const stateResp = await fetch(`/api/async-games/${gameId}/state`, { credentials: 'include' });
      if (!ownsLaunch()) return false;
      if (!stateResp.ok) {
        this.app._flash('Could not download game state');
        return false;
      }

      const stateBuffer = await stateResp.arrayBuffer();
      if (!ownsLaunch()) return false;
      const stateData = this._deserializeState(new Uint8Array(stateBuffer));
      if (!ownsLaunch()) return false;

      this._pendingResume = {
        game,
        state: stateData,
        system_id: game.system_id,
        rom_hash: game.rom_hash,
        core_name: game.core_name || null,
      };

      return true;
    } catch (err) {
      if (!ownsLaunch()) return false;
      console.error('[async-game] loadAndResume failed:', err);
      this.app._flash('Failed to load async game');
      return false;
    }
  }

  consumePendingResume() {
    if (!this._pendingResume) return null;
    const data = this._pendingResume;
    this._pendingResume = null;
    return data;
  }

  applyResume(game, state) {
    // N64 async challenges are unsupported (see endTurn) — nothing to resume.
    if (game?.system_id === 'n64') {
      this.app._flash('Async challenges aren’t available for N64 yet');
      return;
    }
    this.startTurn({
      id: game.id,
      system_id: game.system_id,
      rom_hash: game.rom_hash,
      rom_name: game.rom_name,
    });
    this.app.emu.loadState(state);
  }

  async showChallengePicker(endReason, { isLaunchCurrent = null } = {}) {
    if (this._preparingChallenge || isLaunchCurrent && !isLaunchCurrent()) return false;
    if (this.app.system?.id === 'n64') {
      this.app._flash('Async challenges aren’t available for N64 yet');
      return false;
    }
    const modal = document.getElementById('async-challenge-modal');
    if (!modal) return false;
    this._preparingChallenge = true;
    this._pendingEndReason = endReason;
    this._turnStartFrame = this.app.soloFrame || this.app._libretroSoloFrame || 0;
    this._turnStartTime = this._turnStartTime || Date.now();
    const state = await this._captureBeforePause({ isLaunchCurrent });
    this._preparingChallenge = false;
    if (isLaunchCurrent && !isLaunchCurrent()) return false;
    if (!state) {
      this.app._flash('Could not capture game state');
      return false;
    }
    this._pendingChallengeState = state;
    const statusEl = document.getElementById('async-challenge-status');
    const searchEl = document.getElementById('async-challenge-search');
    if (statusEl) statusEl.textContent = '';
    if (searchEl) searchEl.value = '';
    modal?.classList.remove('hidden');
    this._renderChallengeFriends();
    return true;
  }

  hideChallengePicker() {
    document.getElementById('async-challenge-modal')?.classList.add('hidden');
    this._pendingChallengeState = null;
    this._resumeGame();
  }

  async _renderChallengeFriends() {
    const listEl = document.getElementById('async-challenge-friends');
    if (!listEl) return;

    try {
      const resp = await fetch('/api/friends', { credentials: 'include' });
      if (!resp.ok) {
        listEl.innerHTML = '<p class="invite-friend-empty">Could not load friends</p>';
        return;
      }
      const { friends } = await resp.json();
      if (!friends?.length) {
        listEl.innerHTML = '<p class="invite-friend-empty">No friends yet. Add friends to challenge them!</p>';
        return;
      }
      this._renderFriendRows(listEl, friends);
    } catch {
      listEl.innerHTML = '<p class="invite-friend-empty">Could not load friends</p>';
    }
  }

  _renderFriendRows(listEl, friends) {
    listEl.innerHTML = '';
    for (const f of friends) {
      const row = document.createElement('div');
      row.className = 'friend-row';
      const name = f.displayUsername || f.username;
      const avatar = `<retro-avatar class="friend-avatar" username="${this._esc(f.username)}" size="list" fallback-avatar></retro-avatar>`;
      row.innerHTML = `
        <div class="friend-row-link">
          ${avatar}
          <div class="friend-row-text">
            <span class="friend-row-name">${this._esc(name)}</span>
            <span class="friend-row-presence">@${this._esc(f.username)}</span>
          </div>
        </div>
        <div class="friend-row-actions">
          <button type="button" class="friend-action friend-action-primary">Challenge</button>
        </div>
      `;
      const btn = row.querySelector('button');
      btn.addEventListener('click', () => this._sendChallenge(btn, f));
      listEl.appendChild(row);
    }
  }

  async _sendChallenge(btn, friend) {
    btn.disabled = true;
    btn.textContent = 'Sending...';

    const core = this.app.emu;
    if (!core?.loaded) {
      this.app._flash('No game running');
      btn.disabled = false;
      btn.textContent = 'Challenge';
      return;
    }

    // N64 cannot serialize off the run loop (the loop is paused below), so
    // async challenges are unsupported. See docs/n64-save-handoff.md.
    if (this.app.system?.id === 'n64') {
      this.app._flash('Async challenges aren’t available for N64 yet');
      btn.disabled = false;
      btn.textContent = 'Challenge';
      return;
    }

    const state = this._pendingChallengeState
      || (!this._wasmPaused ? await this._captureBeforePause() : null);
    if (!state) {
      this.app._flash('Could not capture game state');
      btn.disabled = false;
      btn.textContent = 'Challenge';
      return;
    }
    // Keep the captured bytes until the request succeeds so a network/API
    // failure can be retried while the pump core remains paused.
    this._pendingChallengeState = state;

    const stateBytes = this._serializeState(state);
    const frameCount = (this.app.soloFrame || 0) - this._turnStartFrame;
    const durationSeconds = Math.round((Date.now() - this._turnStartTime) / 1000);

    try {
      const params = new URLSearchParams({
        to_username: friend.username,
        system_id: this.app.system?.id || 'nes',
        rom_hash: this.app.romHash || '',
        rom_name: this.app.romName || '',
        core_name: this.app.emu?.coreName || '',
        end_reason: this._pendingEndReason || 'manual',
        frame_count: String(frameCount),
        duration_seconds: String(durationSeconds),
      });

      const resp = await fetch(`/api/async-games?${params}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/octet-stream' },
        body: stateBytes,
      });

      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        const msg = body.error === 'not_friends' ? 'You must be friends to challenge'
          : body.error === 'user_not_found' ? 'User not found'
          : 'Failed to send challenge';
        this.app._flash(msg);
        btn.disabled = false;
        btn.textContent = 'Challenge';
        return;
      }

      const result = await resp.json();
      btn.textContent = 'Sent!';
      this.app._flash(`Challenge sent to ${friend.username}!`, 4000);
      if (result.live_baton) {
        document.getElementById('async-challenge-modal')?.classList.add('hidden');
        this._skipPostEndTurnExit = true;
        if (result.id && typeof this.app._batonBecomeSpectator === 'function') {
          this._liveBatonFallbackTimer = setTimeout(() => {
            this._liveBatonFallbackTimer = null;
            if (!this._skipPostEndTurnExit || this.app.state !== 'solo') return;
            this.app._batonBecomeSpectator({
              type: 'baton_pass',
              role: 'become_spectator',
              game_id: result.id,
              turn_number: 1,
              room_code: AsyncGameManager.roomCodeFor(result.id),
            });
          }, 500);
        }
      } else {
        this.hideChallengePicker();
      }
      this.clear();

      if (!result.live_baton) {
        setTimeout(() => {
          this.app._exitToMenu?.();
        }, 2500);
      }
    } catch (err) {
      console.error('[async-game] challenge failed:', err);
      this.app._flash('Network error. Try again');
      btn.disabled = false;
      btn.textContent = 'Challenge';
    }
  }

  _serializeState(state) {
    if (state instanceof Uint8Array) return state;
    if (ArrayBuffer.isView(state)) return new Uint8Array(state.buffer, state.byteOffset, state.byteLength);
    return new TextEncoder().encode(JSON.stringify(state));
  }

  _deserializeState(bytes) {
    try {
      const text = new TextDecoder().decode(bytes);
      return JSON.parse(text);
    } catch {
      return bytes;
    }
  }

  _esc(s) {
    return escapeHtml(s);
  }
}
